import React, { useState, useEffect } from 'react';
import { nacService, NacNodeData } from '../../services/nac.service';
import './NacDataTable.css';

interface NacDataTableProps {
  period: string;
  company: string;
}

const SEAHSTEEL_COMPANIES = ['세아제강지주', '세아제강', '세아씨엠', '동아스틸'];

const TOTAL_COLS = 18;
const PERSONAL_COLS = 3; // 사용자명, 부서명, 현위치

const NacDataTable: React.FC<NacDataTableProps> = ({ period, company }) => {
  const hidePersonalCols = SEAHSTEEL_COMPANIES.includes(company);
  const colSpan = hidePersonalCols ? TOTAL_COLS - PERSONAL_COLS : TOTAL_COLS;
  const [data, setData] = useState<NacNodeData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [sortBy, setSortBy] = useState('ipv4');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, company, page, limit, sortBy, sortOrder, search]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await nacService.getNacData(
        period,
        company,
        page,
        limit,
        sortBy,
        sortOrder,
        search || undefined
      );
      setData(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to load NAC data:', error);
      alert('데이터를 불러오는데 실패했습니다: ' + (error as any)?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await nacService.exportNacData(period, company);
    } catch (error) {
      console.error('Failed to export NAC data:', error);
      alert('엑셀 다운로드에 실패했습니다: ' + (error as any)?.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="nac-data-table-container">
      <div className="nac-data-header">
        <h3>NAC 노드 데이터 ({company}, {period})</h3>
        <button
          className="export-button"
          onClick={handleExport}
          disabled={isExporting || total === 0}
        >
          {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
        </button>
      </div>

      <div className="nac-data-info">
        <span>총 {total}개 노드</span>
      </div>

      <div className="nac-data-controls">
        <input
          type="text"
          className="search-input"
          placeholder="검색 (IP, 사용자명, 부서 등)..."
          value={search}
          onChange={handleSearchChange}
        />

        <div className="limit-selector">
          <label>표시 개수: </label>
          <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">데이터를 불러오는 중...</div>
      ) : (
        <>
          <div className="table-wrapper">
            <table className="nac-data-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th onClick={() => handleSort('node_type')} className="sortable">
                    노드타입 {sortBy === 'node_type' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th>센서동작</th>
                  <th>에이전트 동작</th>
                  <th>동작</th>
                  <th onClick={() => handleSort('ipv4')} className="sortable">
                    IPv4주소 {sortBy === 'ipv4' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('mac')} className="sortable">
                    MAC주소 {sortBy === 'mac' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  {!hidePersonalCols && <th onClick={() => handleSort('username')} className="sortable">
                    사용자명 {sortBy === 'username' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>}
                  {!hidePersonalCols && <th onClick={() => handleSort('dept')} className="sortable">
                    부서명 {sortBy === 'dept' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>}
                  {!hidePersonalCols && <th>현위치</th>}
                  <th onClick={() => handleSort('policy')} className="sortable">
                    노드정책 {sortBy === 'policy' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th>제어정책</th>
                  <th onClick={() => handleSort('platform')} className="sortable">
                    플랫폼 {sortBy === 'platform' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('sensor')} className="sortable">
                    센서명 {sortBy === 'sensor' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('registered_at')} className="sortable">
                    등록시각 {sortBy === 'registered_at' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('last_auth')} className="sortable">
                    최근 인증 {sortBy === 'last_auth' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('auth_username')} className="sortable">
                    인증사용자명 {sortBy === 'auth_username' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                  <th onClick={() => handleSort('auth_userid')} className="sortable">
                    인증사용자ID {sortBy === 'auth_userid' && (sortOrder === 'asc' ? '▲' : '▼')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={colSpan} className="no-data">데이터가 없습니다</td>
                  </tr>
                ) : (
                  data.map((item, index) => (
                    <tr key={index}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{item.node_type}</td>
                      <td>{item.sensor_status}</td>
                      <td>{item.agent_status}</td>
                      <td className={item.status === 'UP' ? 'status-up' : 'status-down'}>
                        {item.status}
                      </td>
                      <td>{item.ipv4}</td>
                      <td>{item.mac}</td>
                      {!hidePersonalCols && <td>{item.username}</td>}
                      {!hidePersonalCols && <td>{item.dept}</td>}
                      {!hidePersonalCols && <td>{item.location}</td>}
                      <td>{item.policy}</td>
                      <td>{item.control_policy}</td>
                      <td>{item.platform}</td>
                      <td>{item.sensor}</td>
                      <td>{item.registered_at}</td>
                      <td>{item.last_auth}</td>
                      <td>{item.auth_username}</td>
                      <td>{item.auth_userid}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              처음
            </button>
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              이전
            </button>
            <span className="page-info">
              {page} / {totalPages} 페이지
            </span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
            >
              다음
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
            >
              마지막
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default NacDataTable;
